import { BellOutlined } from "@ant-design/icons";
import { Button, Empty, Spin } from "antd";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { AlertCircle, Trash2Icon } from "lucide-react";
import { useMemo } from "react";
import { useDeleteNotification } from "../../api/api.delete";
import { useGetNotifications } from "../../api/api.get";
import { useNotifications } from "../../components/notifications/notification";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { INotification, TRANSACTION_TYPE } from "../../types/types";

dayjs.extend(relativeTime);

export default function NotificationsPage() {
  const { notification, notifyError } = useNotifications();
  const { data: notificationsData, isLoading, refetch } = useGetNotifications();

  const notifications = useMemo(() => {
    if (notificationsData) {
      return notificationsData.filter(
        (n) => n.type === TRANSACTION_TYPE.PLAID_GET_EXPENSE_ERROR,
      );
    }
    return [];
  }, [notificationsData]);

  return (
    <div className="min-h-screen bg-slate-50">
      {notification}

      <main className="pt-8 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="mb-8 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <BellOutlined className="text-2xl text-primary" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-foreground">
                Notifications
              </h1>
            </div>
          </div>

          {isLoading && (
            <div className="flex justify-center py-20">
              <Spin size="large" />
            </div>
          )}

          {!isLoading && (!notifications || notifications.length === 0) && (
            <Card className="bg-white">
              <CardContent className="py-16">
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={
                    <span className="text-muted-foreground">
                      No notifications yet
                    </span>
                  }
                />
              </CardContent>
            </Card>
          )}

          {!isLoading && notifications && notifications.length > 0 && (
            <div className="space-y-6">
              {/* Unread */}
              <Card className="bg-white">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <span className="inline-block w-2 h-2 rounded-full bg-primary" />
                    New
                  </CardTitle>
                  <CardDescription>
                    {notifications.length} unread
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {notifications.map((item) => (
                      <NotificationRow
                        key={item.id}
                        item={item}
                        onDeleteSuccess={() => refetch()}
                        onDeleteError={() =>
                          notifyError("Whoops! Please try again.")
                        }
                      />
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

function NotificationRow({
  item,
  onDeleteSuccess,
  onDeleteError,
}: {
  onDeleteSuccess: () => void;
  onDeleteError: () => void;
  item: INotification;
  dimmed?: boolean;
}) {
  const { mutateAsync: deleteAsync } = useDeleteNotification();
  const transactionTypeTitle = useMemo(() => {
    if (item.type === TRANSACTION_TYPE.PLAID_GET_EXPENSE_ERROR) {
      return "Bank Sync Error";
    }
    return "Unknown";
  }, [item.type]);

  const transactionTypeSubTitle = useMemo(() => {
    if (item.type === TRANSACTION_TYPE.PLAID_GET_EXPENSE_ERROR) {
      return "Please go to Accounts and click the three dots. Select 'Resync With Bank' to fix this problem.";
    }
    return "Please contact support at help@expensehut.com";
  }, [item.type]);

  const handleDelete = (id: string) => {
    deleteAsync({ transactionId: id })
      .then(onDeleteSuccess)
      .catch(onDeleteError);
  };

  return (
    <div className={`flex items-start gap-4 p-4 rounded-lg border transition`}>
      <div className="mt-0.5 shrink-0">
        <AlertCircle />
      </div>

      <div className="flex-1 min-w-0">
        <p className={`text-sm leading-relaxed font-semibold text-red-500`}>
          {transactionTypeTitle}
        </p>

        <p className="text-xs">{transactionTypeSubTitle}</p>
        <p className={`text-xs mt-1`}>
          {dayjs(item.timestamp).format("DD MMM YYYY hh:mmA")}
        </p>
      </div>

      <Button
        icon={<Trash2Icon size={15} />}
        onClick={() => handleDelete(item.id)}
      />
    </div>
  );
}
